import { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Field, Fieldset, Label, Paragraph, Radio, Textarea, ValidationMessage } from '@digdir/designsystemet-react';
import type { HmsApprovalMode } from '../../../api/hms';
import { useUsesSafetyReps } from '../../../hooks/queries/hms/useHmsSettingsQuery';
import classes from './ApprovalChoice.module.css';

/** The choice of who approves, and HR's comment when approving without the safety representative */
export type ApprovalFormState = {
  hasMainSafetyRep: boolean;
  /** The HR user is the main safety representative: they approve as HR, with a reason */
  mainSafetyRepIsYou?: boolean;
  mode: HmsApprovalMode;
  onModeChange: (mode: HmsApprovalMode) => void;
  comment: string;
  onCommentChange: (value: string) => void;
  commentError?: string;
};

type Props = ApprovalFormState & {
  name: string;
  legend: string;
  commentId: string;
  commentDescription: string;
};

export const ApprovalChoice = ({
  name,
  legend,
  commentId,
  commentDescription,
  hasMainSafetyRep,
  mainSafetyRepIsYou,
  mode,
  onModeChange,
  comment,
  onCommentChange,
  commentError,
}: Props): ReactElement => {
  const { t } = useTranslation();
  const usesSafetyReps = useUsesSafetyReps();

  if (!hasMainSafetyRep && mainSafetyRepIsYou) {
    return (
      <Fieldset className={classes.fieldset}>
        <Fieldset.Legend>{legend}</Fieldset.Legend>
        <Paragraph data-size="sm">{t('hms.approval.youAreMain')}</Paragraph>
        <ApprovalComment
          id={commentId}
          description={t('hms.approval.youAreMainComment')}
          value={comment}
          onChange={onCommentChange}
          error={commentError}
        />
      </Fieldset>
    );
  }

  if (!hasMainSafetyRep) {
    return (
      <Fieldset className={classes.fieldset}>
        <Fieldset.Legend>{legend}</Fieldset.Legend>
        <Paragraph data-size="sm">{t(`hms.approval.${usesSafetyReps ? 'noSafetyRep' : 'notUsed'}`)}</Paragraph>
      </Fieldset>
    );
  }

  return (
    <Fieldset className={classes.fieldset}>
      <Fieldset.Legend>{legend}</Fieldset.Legend>
      <Radio
        data-size="sm"
        name={name}
        value="SAFETY_REP"
        label={t('hms.approval.safetyRep')}
        checked={mode === 'SAFETY_REP'}
        onChange={() => onModeChange('SAFETY_REP')}
      />
      <Radio
        data-size="sm"
        name={name}
        value="HR_WITHOUT_SAFETY_REP"
        label={t('hms.approval.hr')}
        checked={mode === 'HR_WITHOUT_SAFETY_REP'}
        onChange={() => onModeChange('HR_WITHOUT_SAFETY_REP')}
      />
      {mode === 'HR_WITHOUT_SAFETY_REP' && (
        <ApprovalComment
          id={commentId}
          description={commentDescription}
          value={comment}
          onChange={onCommentChange}
          error={commentError}
        />
      )}
    </Fieldset>
  );
};

type CommentProps = {
  id: string;
  label?: string;
  description: string;
  value: string;
  onChange: (value: string) => void;
  error?: string;
};

export const ApprovalComment = ({ id, label, description, value, onChange, error }: CommentProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Field data-size="sm" className={classes.comment}>
      <Label htmlFor={id}>{label ?? t('hms.approval.comment')}</Label>
      <Field.Description id={`${id}-description`}>{description}</Field.Description>
      <Textarea
        id={id}
        rows={2}
        aria-describedby={`${id}-description`}
        aria-invalid={Boolean(error) || undefined}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
      {error && <ValidationMessage>{error}</ValidationMessage>}
    </Field>
  );
};
